import { Breadcrumbs, Link, Typography, Box, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "@app/hooks/useBreadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useAppContext } from "@app/AppContext";
import { useTheme } from "@mui/material/styles";

export function Breadcrumb() {
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();
  const { groupName, groupLogoUrl, machineName } = useAppContext();
  const theme = useTheme();

  const isRtl = theme.direction === "rtl";

  // Build display breadcrumbs with actual names
  const displayBreadcrumbs = breadcrumbs.map((crumb) => {
    let label = crumb.label;
    let isLoading = false;

    // Replace "Group" with actual group name if available
    if (crumb.label === "Group") {
      if (groupName) {
        label = groupName;
      } else {
        isLoading = true;
      }
    }

    // Replace "Machine" with actual machine name if available
    if (crumb.label === "Machine") {
      if (machineName) {
        label = machineName;
      } else {
        isLoading = true;
      }
    }

    return { ...crumb, label, isLoading };
  });

  if (displayBreadcrumbs.length <= 1) {
    return null;
  }

  const isOnGroupDetailRoute = breadcrumbs.some(
    (crumb) => crumb.label === "Group" && crumb.isActive,
  );
  const isGroupLogoLoading = isOnGroupDetailRoute && !groupName;

  const SeparatorIcon = isRtl ? NavigateBeforeIcon : NavigateNextIcon;

  return (
    <Box
      sx={{
        px: 3,
        pt: 1.5,
        backgroundColor: "#f5f6f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Breadcrumbs
        separator={<SeparatorIcon sx={{ fontSize: "1rem" }} />}
        aria-label="breadcrumb"
      >
        {displayBreadcrumbs.map((breadcrumb) => {
          const isLast = breadcrumb.isActive;

          return isLast ? (
            <Typography
              key={breadcrumb.path}
              sx={{
                color: "text.primary",
                fontWeight: 500,
              }}
            >
              {breadcrumb.isLoading ? (
                <Skeleton width="100px" height="20px" />
              ) : (
                breadcrumb.label
              )}
            </Typography>
          ) : (
            <Link
              key={breadcrumb.path}
              component="button"
              variant="body2"
              onClick={() => navigate(breadcrumb.path)}
              sx={{
                cursor: "pointer",
                color: "#140a9a",
                textDecoration: "none",
                backgroundColor: "transparent",
                "&:hover": {
                  textDecoration: "underline",
                },
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: "none",
                padding: 0,
                font: "inherit",
              }}
            >
              {breadcrumb.isLoading ? (
                <Skeleton width="80px" height="20px" />
              ) : (
                breadcrumb.label
              )}
            </Link>
          );
        })}
      </Breadcrumbs>

      {isGroupLogoLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{ width: "160px", height: "48px", borderRadius: "4px" }}
        />
      ) : (
        groupLogoUrl && (
          <Box
            component="img"
            src={groupLogoUrl}
            alt=""
            sx={{
              width: "160px",
              height: "48px",
              objectFit: "contain",
            }}
          />
        )
      )}
    </Box>
  );
}
